import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { toast } from "react-toastify";
import { MenuItem, Box, Typography, Checkbox, FormControlLabel, IconButton } from "@mui/material";
import { C, fmtINR } from "../constants";
import { IPD_COLORS } from "../../ipd/utils/ipdColors";
import IpdTextField from "../../ipd/utils/IpdTextField";
import IpdDateField from "../../ipd/utils/IpdDateField";
import IpdFormField from "../../ipd/utils/IpdFormField";
import IpdButton from "../../ipd/utils/IpdButton";
import IpdSectionCard from "../../ipd/utils/IpdSectionCard";
import IpdConfirmDialog from "../../ipd/utils/IpdConfirmDialog";
import IpdDataGrid from "../../ipd/utils/IpdDataGrid";
import SvgIcon from "../SvgIcon";
import {
  getPackage,
  createPackage,
  updatePackageHeader,
  createPackageVersion,
  listPackageVersions,
  updatePackageVersion,
  publishPackageVersion,
  listPackageTariffs,
  createPackageTariff,
  updatePackageTariff,
  deletePackageTariff,
  listPackageComponents,
  createPackageComponent,
  updatePackageComponent,
  deletePackageComponent,
  listChargeHeads,
  createChargeHead,
} from "../package-billing-api";
import {
  listServiceGroups,
  listServiceClassifications,
  listMedicalServices,
  listInventoryMaterialGroups,
  listInventoryItemClasses,
  listInventoryCategories,
  searchInventoryItems,
  toBillingError,
} from "../billing-api";
import { listPayersByFinanceClass } from "../corporate-api";
import { listLookups } from "../service-master-api";

const todayISO = () => new Date().toISOString().slice(0, 10);

// Format an ISO date (YYYY-MM-DD) as "DD MMM YYYY"; blank renders as an em-dash.
const fmtDate = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  return isNaN(d.getTime()) ? v : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

const STATUS_TONE = {
  ACTIVE: { bg: IPD_COLORS.success.bg, color: IPD_COLORS.success.text },
  DRAFT: { bg: "#e0e7ff", color: "#4338ca" }, // indigo — no matching hue family in IPD_COLORS, left as-is
  PUBLISHED: { bg: IPD_COLORS.success.bg, color: IPD_COLORS.success.text },
  INACTIVE: { bg: IPD_COLORS.pageBg, color: IPD_COLORS.textSecondary },
  RETIRED: { bg: IPD_COLORS.error.bg, color: IPD_COLORS.error.text },
};

function StatusBadge({ status }) {
  const tone = STATUS_TONE[status] || STATUS_TONE.INACTIVE;
  return (
    <Box component="span" sx={{ display: "inline-block", padding: "3px 9px", borderRadius: "999px", fontSize: "11px", fontWeight: 700, backgroundColor: tone.bg, color: tone.color }}>
      {status || "—"}
    </Box>
  );
}

function LockedNotice({ children }) {
  return (
    <Box sx={{ marginBottom: "16px", padding: "12px 16px", backgroundColor: IPD_COLORS.warning.bg, border: `1px solid ${IPD_COLORS.warning.border}`, borderRadius: "8px", fontSize: "12px", color: IPD_COLORS.warning.text, display: "flex", alignItems: "center", gap: "8px" }}>
      <SvgIcon name="shield" size={14} color={IPD_COLORS.warning.text} />
      {children}
    </Box>
  );
}

// ── Header tab ────────────────────────────────────────────────────────────────
// Duration/effective_from are stored on the package's v1 version by the real
// API (not the package header) — `version` should be the first/only version
// when creating, so its values seed the form's create-only duration fields.
function HeaderForm({ packageId, pkg, version, onCreated, onUpdated }) {
  const [form, setForm] = useState({
    package_code: "",
    package_name: "",
    package_category: "GENERAL",
    encounter_type: "IPD",
    is_multi_visit: false,
    effective_from: todayISO(),
    effective_to: "",
    published_at: "",
    duration_value: 5,
    duration_unit: "DAY",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!pkg) return;
    setForm({
      package_code: pkg.code,
      package_name: pkg.name,
      package_category: pkg.category || "GENERAL",
      encounter_type: pkg.encounterType || "IPD",
      is_multi_visit: Boolean(pkg.isMultiVisit),
      effective_from: version?.effectiveFrom || todayISO(),
      effective_to: version?.effectiveTo ?? "",
      published_at: version?.publishedAt ?? "",
      duration_value: version?.durationValue ?? 5,
      duration_unit: version?.durationUnit || "DAY",
    });
  }, [pkg, version]);

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = async () => {
    if (!form.package_code.trim()) { toast.error("Package code is required"); return; }
    if (form.effective_to && form.effective_from && form.effective_to < form.effective_from) {
      toast.error("Effective to must be on or after effective from"); return;
    }
    setSaving(true);
    try {
      if (packageId) {
        await updatePackageHeader(packageId, form);
        if (version) {
          const versionBody = {};
          if (form.effective_from !== version.effectiveFrom) versionBody.effective_from = form.effective_from;
          if (form.effective_to !== version.effectiveTo) versionBody.effective_to = form.effective_to || null;
          if (form.published_at !== version.publishedAt) versionBody.published_at = form.published_at || undefined;
          if (Object.keys(versionBody).length > 0) {
            await updatePackageVersion(version.id, versionBody);
          }
        }
        toast.success("Package header updated");
        onUpdated();
      } else {
        const res = await createPackage(form);
        // POST /packages/ only accepts effective_from; effective_to has to be
        // applied to the auto-created v1 version afterwards.
        if (form.effective_to) {
          // The package itself already exists at this point, so a failure here
          // must not abort the create — warn and let the user retry on the header.
          try {
            const v1 = (await listPackageVersions(res.package.id))[0];
            if (v1) {
              await updatePackageVersion(v1.id, { effective_to: form.effective_to });
            }
          } catch (err) {
            toast.warn(toBillingError(err, "Package created, but the effective-to date could not be saved"));
          }
        }
        toast.success(`Package "${res.package.code}" created — draft v1 ready`);
        onCreated(res.package.id);
      }
    } catch (err) {
      toast.error(toBillingError(err, "Could not save package"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <IpdSectionCard
      title={packageId ? "Package header" : "New package"}
      subtitle="Marketing / clinical definition — code and name shown to billing staff"
      actions={
        <IpdButton onClick={submit} loading={saving}>
          {packageId ? "Save changes" : "Create package"}
        </IpdButton>
      }
    >
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "16px" }}>
        <IpdFormField label="Package code" required>
          <IpdTextField fullWidth value={form.package_code} onChange={set("package_code")} placeholder="CARDIAC-5D" disabled={Boolean(packageId)} />
        </IpdFormField>
        <IpdFormField label="Package name">
          <IpdTextField fullWidth value={form.package_name} onChange={set("package_name")} placeholder="Cardiac Package - 5 Days" />
        </IpdFormField>
        <IpdFormField label="Category">
          <IpdTextField fullWidth value={form.package_category} onChange={set("package_category")} placeholder="CARDIAC" />
        </IpdFormField>
        <IpdFormField label="Encounter type">
          <IpdTextField select fullWidth value={form.encounter_type} onChange={set("encounter_type")}>
            {["IPD", "OPD", "ALL"].map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </IpdTextField>
        </IpdFormField>
        <IpdFormField label={`Duration${packageId ? " (set on version — edit via a new draft version)" : ""}`}>
          <Box sx={{ display: "flex", gap: "8px" }}>
            <IpdTextField type="number" value={form.duration_value} onChange={set("duration_value")} disabled={Boolean(packageId)} />
            <IpdTextField select value={form.duration_unit} onChange={set("duration_unit")} disabled={Boolean(packageId)} sx={{ width: "110px", flexShrink: 0 }}>
              {["DAY", "VISIT"].map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </IpdTextField>
          </Box>
        </IpdFormField>
        <IpdFormField label="Effective from">
          <IpdDateField value={form.effective_from} onChange={(iso) => set("effective_from")({ target: { value: iso } })} />
        </IpdFormField>
        <IpdFormField label="Effective to (optional)">
          <IpdDateField value={form.effective_to} onChange={(iso) => set("effective_to")({ target: { value: iso } })} />
        </IpdFormField>
        <IpdFormField label="Published at (optional)">
          <IpdDateField value={form.published_at} onChange={(iso) => set("published_at")({ target: { value: iso } })} disabled={!packageId || !version} />
        </IpdFormField>
        <Box sx={{ display: "flex", alignItems: "flex-end" }}>
          <FormControlLabel
            control={<Checkbox size="small" checked={form.is_multi_visit} onChange={set("is_multi_visit")} />}
            label="Multi-visit package"
            sx={{ m: 0, "& .MuiFormControlLabel-label": { fontSize: "13px", color: C.text } }}
          />
        </Box>
      </Box>
      {!packageId && (
        <Box sx={{ marginTop: "16px", padding: "12px 16px", backgroundColor: IPD_COLORS.info.bg, border: `1px solid ${C.border}`, borderRadius: "8px", fontSize: "12px", color: C.textSub }}>
          Creating the package auto-creates draft version 1 — Tariffs and Components tabs unlock once saved.
        </Box>
      )}
    </IpdSectionCard>
  );
}

// ── Versions tab ─────────────────────────────────────────────────────────────
function VersionsTab({ packageId, versions, currentVersionId, onSelectVersion, onVersionsChanged }) {
  const [creating, setCreating] = useState(false);
  const latest = versions[0];
  const canCreateDraft = latest && latest.status === "PUBLISHED";

  const createDraft = async () => {
    setCreating(true);
    try {
      const v = await createPackageVersion(packageId);
      toast.success(`Draft v${v.versionNumber} created from the published version`);
      await onVersionsChanged();
      onSelectVersion(v.id);
    } catch (err) {
      toast.error(toBillingError(err, "Could not create new version"));
    } finally {
      setCreating(false);
    }
  };

  const columns = [
    { field: "versionNumber", headerName: "Version", flex: 1, minWidth: 90, sortable: false,
      renderCell: (p) => <Box component="span" sx={{ fontWeight: 700, color: C.text }}>v{p.value}</Box> },
    { field: "status", headerName: "Status", flex: 1, minWidth: 110, sortable: false,
      renderCell: (p) => <StatusBadge status={p.value} /> },
    { field: "publishedAt", headerName: "Published", flex: 1, minWidth: 120, sortable: false,
      renderCell: (p) => <Box component="span" sx={{ fontSize: "12px", color: C.textSub }}>{p.value ? new Date(p.value).toLocaleDateString("en-IN") : "—"}</Box> },
    { field: "actions", headerName: "", flex: 1, minWidth: 150, sortable: false, align: "right", headerAlign: "right",
      renderCell: (p) => (
        <IpdButton variant="outlined" size="small" onClick={() => onSelectVersion(p.row.id)}>
          {p.row.id === currentVersionId ? "Editing" : "Edit this version"}
        </IpdButton>
      ) },
  ];

  return (
    <IpdSectionCard
      title="Versions"
      subtitle="Published versions are editable; this version's tariff and component changes apply to future admissions."
      actions={
        <IpdButton
          variant="outlined"
          size="small"
          onClick={createDraft}
          disabled={!canCreateDraft || creating}
          loading={creating}
          startIcon={<SvgIcon name="plus" size={14} color={C.text} />}
          title={!canCreateDraft ? "Publish the current version first" : ""}
        >
          New draft version
        </IpdButton>
      }
    >
      <Box sx={{ "& .ipd-row-current": { backgroundColor: C.accent } }}>
        <IpdDataGrid
          embedded
          hideFooter
          disableColumnMenu
          rows={versions}
          columns={columns}
          getRowClassName={(p) => (p.row.id === currentVersionId ? "ipd-row-current" : "")}
          emptyMessage="No versions yet"
        />
      </Box>
    </IpdSectionCard>
  );
}

// ── Tariffs tab ──────────────────────────────────────────────────────────────
const TARIFF_TYPES = ["HOSPITAL", "CORPORATE", "TPA", "INSURANCE", "GOVERNMENT"];

function TariffForm({ versionId, onSaved }) {
  const [form, setForm] = useState({
    tariff_type: "HOSPITAL",
    payer_id: "",
    bed_type_code: "",
    tariff_name: "",
    package_price: "",
    standard_package_price: "",
    payer_discount_amount: "",
    effective_from: todayISO(),
    effective_to: "",
  });
  const [payers, setPayers] = useState([]);
  const [bedTypes, setBedTypes] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (form.tariff_type === "HOSPITAL") return;
    // Show the SAME full payer catalog the admission page uses (filtered by finance class),
    // not just the handful of payers already synced to billing. The picked name is resolved
    // to the matching billing payer on save. tariff_type maps to the catalog's finance class.
    const FINANCE_CLASS = { CORPORATE: "CORP", INSURANCE: "INSURANCE", TPA: "TPA", GOVERNMENT: "GOVT_SCHEME" };
    const financeClass = FINANCE_CLASS[form.tariff_type];
    if (!financeClass) return;
    (async () => {
      try { setPayers(await listPayersByFinanceClass(financeClass)); } catch { /* non-fatal — payer dropdown just stays empty */ }
    })();
    // The selected payer belongs to the previous type's list; clear it so a stale payer
    // from another type can't be submitted.
    setForm((f) => (f.payer_id ? { ...f, payer_id: "" } : f));
  }, [form.tariff_type]);

  // Bed types — a package can hold several tariffs of the same type differing only by bed
  // type so the fixed price varies by room class (blank = any bed).
  useEffect(() => {
    let cancelled = false;
    listLookups({ activeOnly: true })
      .then(({ bedTypes: bt }) => { if (!cancelled) setBedTypes(bt); })
      .catch(() => { /* non-fatal */ });
    return () => { cancelled = true; };
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.package_price) { toast.error("Package price is required"); return; }
    if (form.tariff_type !== "HOSPITAL" && !form.payer_id) { toast.error("Select a payer for a non-hospital tariff"); return; }
    if (form.effective_to && form.effective_to < form.effective_from) { toast.error("Effective to must be on or after effective from"); return; }
    setSaving(true);
    try {
      const payer = payers.find((p) => p.id === form.payer_id);
      await createPackageTariff(versionId, {
        tariff_type: form.tariff_type,
        payer_id: form.payer_id || null,
        payer_name: payer?.name || null,
        bed_type_code: form.bed_type_code || null,
        tariff_name: form.tariff_name.trim() || undefined,
        package_price: form.package_price,
        standard_package_price: form.standard_package_price || form.package_price,
        payer_discount_amount: form.payer_discount_amount || undefined,
        effective_from: form.effective_from,
        effective_to: form.effective_to || undefined,
        priority: 100, // fixed default — tariff priority is no longer user-editable
      });
      toast.success("Tariff added");
      setForm((f) => ({ ...f, payer_id: "", bed_type_code: "", tariff_name: "", package_price: "", standard_package_price: "", payer_discount_amount: "", effective_to: "" }));
      onSaved();
    } catch (err) {
      toast.error(toBillingError(err, "Could not add tariff"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "12px", padding: "14px", backgroundColor: IPD_COLORS.pageBg, border: `1px solid ${C.border}`, borderRadius: "10px", marginBottom: "16px" }}>
      <IpdFormField label="Tariff type">
        <IpdTextField select fullWidth value={form.tariff_type} onChange={set("tariff_type")}>
          {TARIFF_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
        </IpdTextField>
      </IpdFormField>
      {form.tariff_type !== "HOSPITAL" && (
        <IpdFormField label="Payer" required>
          <IpdTextField select fullWidth value={form.payer_id} onChange={set("payer_id")}>
            <MenuItem value="">Select payer…</MenuItem>
            {payers.map((p) => <MenuItem key={p.id} value={p.id}>{p.name} ({p.code})</MenuItem>)}
          </IpdTextField>
        </IpdFormField>
      )}
      {/* displayEmpty so the "" default renders as "Any bed type" instead of a blank box */}
      <IpdFormField label="Bed type (blank = any bed)">
        <IpdTextField select fullWidth value={form.bed_type_code} onChange={set("bed_type_code")} SelectProps={{ displayEmpty: true }}>
          <MenuItem value="">Any bed type</MenuItem>
          {bedTypes.map((b) => <MenuItem key={b.id} value={b.code}>{b.name} ({b.code})</MenuItem>)}
        </IpdTextField>
      </IpdFormField>
      <IpdFormField label="Tariff name">
        <IpdTextField fullWidth value={form.tariff_name} onChange={set("tariff_name")} placeholder="Standard hospital rate" />
      </IpdFormField>
      <IpdFormField label="Package price" required>
        <IpdTextField fullWidth type="number" value={form.package_price} onChange={set("package_price")} placeholder="100000" />
      </IpdFormField>
      <IpdFormField label="Standard price (for savings display)">
        <IpdTextField fullWidth type="number" value={form.standard_package_price} onChange={set("standard_package_price")} placeholder="100000" />
      </IpdFormField>
      {form.tariff_type !== "HOSPITAL" && (
        <IpdFormField label="Discount amount">
          <IpdTextField fullWidth type="number" value={form.payer_discount_amount} onChange={set("payer_discount_amount")} placeholder="22000" />
        </IpdFormField>
      )}
      <IpdFormField label="Effective from">
        <IpdDateField value={form.effective_from} onChange={(iso) => set("effective_from")({ target: { value: iso } })} />
      </IpdFormField>
      <IpdFormField label="Effective to (optional)">
        <IpdDateField value={form.effective_to} onChange={(iso) => set("effective_to")({ target: { value: iso } })} />
      </IpdFormField>
      <Box sx={{ display: "flex", alignItems: "flex-end" }}>
        <IpdButton onClick={submit} loading={saving} sx={{ width: "100%", justifyContent: "center" }}>
          Add tariff
        </IpdButton>
      </Box>
    </Box>
  );
}

// Inline edit for a saved tariff (bed type, price, standard price, effective dates).
// Uses POST /package-tariffs/{id}/update/. Tariff type + payer are identity and stay fixed.
function TariffEditForm({ tariff, onSaved, onClose }) {
  const [form, setForm] = useState({
    bed_type_code: tariff.bedType || "",
    package_price: tariff.packagePrice != null ? String(tariff.packagePrice) : "",
    standard_package_price: tariff.standardPackagePrice != null ? String(tariff.standardPackagePrice) : "",
    effective_from: tariff.effectiveFrom || todayISO(),
    effective_to: tariff.effectiveTo || "",
  });
  const [bedTypes, setBedTypes] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    listLookups({ activeOnly: true })
      .then(({ bedTypes: bt }) => { if (!cancelled) setBedTypes(bt); })
      .catch(() => { /* non-fatal */ });
    return () => { cancelled = true; };
  }, []);

  const save = async () => {
    if (!form.package_price) { toast.error("Package price is required"); return; }
    setSaving(true);
    try {
      await updatePackageTariff(tariff.id, {
        bed_type_code: form.bed_type_code || null,
        package_price: form.package_price,
        standard_package_price: form.standard_package_price || form.package_price,
        effective_from: form.effective_from,
        effective_to: form.effective_to || null,
      });
      toast.success("Tariff updated");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(toBillingError(err, "Could not update tariff"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: "12px", padding: "14px", backgroundColor: IPD_COLORS.info.bg, border: `1px solid ${IPD_COLORS.info.border}`, borderRadius: "10px", marginBottom: "16px" }}>
      <Box sx={{ flexBasis: "100%", fontSize: "12px", fontWeight: 700, color: C.text }}>
        Edit tariff — {tariff.tariffType}{tariff.payerName ? ` · ${tariff.payerName}` : ""} (type/payer fixed)
      </Box>
      <IpdFormField label="Bed type (blank = any bed)" sx={{ flex: "1 1 150px" }}>
        <IpdTextField select fullWidth value={form.bed_type_code} onChange={(e) => setForm((f) => ({ ...f, bed_type_code: e.target.value }))}>
          <MenuItem value="">Any bed type</MenuItem>
          {bedTypes.map((b) => <MenuItem key={b.id} value={b.code}>{b.name} ({b.code})</MenuItem>)}
        </IpdTextField>
      </IpdFormField>
      <IpdFormField label="Package price" required sx={{ flex: "1 1 150px" }}>
        <IpdTextField fullWidth type="number" value={form.package_price} onChange={(e) => setForm((f) => ({ ...f, package_price: e.target.value }))} />
      </IpdFormField>
      <IpdFormField label="Standard price" sx={{ flex: "1 1 150px" }}>
        <IpdTextField fullWidth type="number" value={form.standard_package_price} onChange={(e) => setForm((f) => ({ ...f, standard_package_price: e.target.value }))} />
      </IpdFormField>
      <IpdFormField label="Effective from" sx={{ flex: "1 1 150px" }}>
        <IpdDateField value={form.effective_from} onChange={(iso) => setForm((f) => ({ ...f, effective_from: iso }))} />
      </IpdFormField>
      <IpdFormField label="Effective to (optional)" sx={{ flex: "1 1 150px" }}>
        <IpdDateField value={form.effective_to} onChange={(iso) => setForm((f) => ({ ...f, effective_to: iso }))} />
      </IpdFormField>
      <Box sx={{ display: "flex", gap: "8px", marginLeft: "auto", paddingBottom: "20px" }}>
        <IpdButton variant="outlined" onClick={onClose} sx={{ justifyContent: "center" }}>Cancel</IpdButton>
        <IpdButton onClick={save} loading={saving} sx={{ justifyContent: "center" }}>Save</IpdButton>
      </Box>
    </Box>
  );
}

function TariffsTab({ versionId, isDraft }) {
  const [tariffs, setTariffs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [editTariff, setEditTariff] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setTariffs(await listPackageTariffs(versionId)); } catch { setTariffs([]); }
    finally { setLoading(false); }
  }, [versionId]);

  useEffect(() => { load(); }, [load]);

  const remove = async (t) => {
    setBusyId(t.id);
    try {
      await deletePackageTariff(t.id);
      toast.success("Tariff removed");
      await load();
    } catch (err) {
      toast.error(toBillingError(err, "Could not remove tariff"));
    } finally {
      setBusyId(null);
    }
  };

  const hasHospital = tariffs.some((t) => t.tariffType === "HOSPITAL" && t.active);

  const columns = [
    { field: "tariffType", headerName: "Type", flex: 1, minWidth: 110, sortable: false,
      renderCell: (p) => <Box component="span" sx={{ fontSize: "12px", fontWeight: 700, color: C.text }}>{p.value}</Box> },
    { field: "bedType", headerName: "Bed type", flex: 0.8, minWidth: 90, sortable: false,
      renderCell: (p) => <Box component="span" sx={{ fontSize: "12px", color: C.textSub }}>{p.value || "Any"}</Box> },
    { field: "name", headerName: "Name", flex: 1.4, minWidth: 150, sortable: false },
    { field: "packagePrice", headerName: "Package price", flex: 1, minWidth: 130, sortable: false, align: "right", headerAlign: "right",
      renderCell: (p) => <Box component="span" sx={{ fontSize: "13px", fontWeight: 700, color: C.text }}>{fmtINR(p.value)}</Box> },
    { field: "standardPackagePrice", headerName: "Standard price", flex: 1, minWidth: 130, sortable: false, align: "right", headerAlign: "right",
      renderCell: (p) => <Box component="span" sx={{ fontSize: "12px", color: C.textSub }}>{p.value != null ? fmtINR(p.value) : "—"}</Box> },
    { field: "effectiveFrom", headerName: "Effective from", flex: 0.9, minWidth: 120, sortable: false,
      renderCell: (p) => <Box component="span" sx={{ fontSize: "12px", color: C.textSub, whiteSpace: "nowrap" }}>{fmtDate(p.value)}</Box> },
    { field: "effectiveTo", headerName: "Effective to", flex: 0.9, minWidth: 120, sortable: false,
      renderCell: (p) => <Box component="span" sx={{ fontSize: "12px", color: C.textSub, whiteSpace: "nowrap" }}>{fmtDate(p.value)}</Box> },
    { field: "actions", headerName: "", width: 92, sortable: false, align: "right", headerAlign: "right",
      renderCell: (p) => (
        <Box sx={{ display: "flex", gap: "2px" }}>
          <IconButton size="small" onClick={() => setEditTariff(p.row)} disabled={busyId === p.row.id} title="Edit">
            <SvgIcon name="edit" size={16} color={C.primary} />
          </IconButton>
          <IconButton size="small" onClick={() => remove(p.row)} disabled={busyId === p.row.id} title="Remove">
            <SvgIcon name="trash" size={16} color={C.danger} />
          </IconButton>
        </Box>
      ) },
  ];

  return (
    <IpdSectionCard title="Tariffs" subtitle="At least one active HOSPITAL tariff is required before publish">
      {!hasHospital && <LockedNotice>No active HOSPITAL tariff yet — publish will be blocked until one exists.</LockedNotice>}
      <TariffForm versionId={versionId} onSaved={load} />

      {editTariff && (
        <TariffEditForm
          tariff={editTariff}
          onSaved={load}
          onClose={() => setEditTariff(null)}
        />
      )}

      <IpdDataGrid
        embedded
        hideFooter
        disableColumnMenu
        loading={loading}
        rows={tariffs}
        columns={columns}
        emptyMessage="No tariffs yet"
      />
    </IpdSectionCard>
  );
}

// ── Components tab ───────────────────────────────────────────────────────────
const COMPONENT_LEVELS = ["HEAD", "SERVICE_GROUP", "CLASSIFICATION", "SERVICE", "PRODUCT", "ROOM_CATEGORY"];
const LIMIT_TYPES = ["NO_LIMIT", "ITEM_LIMIT", "AMOUNT_LIMIT"];
const EXCESS_HANDLING = ["OUT_OF_PACKAGE", "BLOCK", "APPROVAL_REQUIRED"];

function componentDepth(component, byId, guard = 0) {
  if (!component?.parentComponentId || guard > 8) return 0;
  const parent = byId.get(component.parentComponentId);
  if (!parent) return 0;
  return 1 + componentDepth(parent, byId, guard + 1);
}

// Adds a component, and edits one that is already saved — the same form either way.
// `editing` is the saved component, or null to add. It is mounted under a key that changes
// with it, so the state below starts from whichever component is being worked on.
//
// Editing used to be a cut-down form that held the level and the reference target fixed,
// which meant a component pointing at the wrong thing (or at nothing) could not be
// corrected at all: the only way out was to add a second one and leave the first in place.
// That is what made an edit look like it had duplicated the component.
function ComponentForm({ versionId, existingComponents, onSaved, editing = null, onCancelEdit }) {
  const [level, setLevel] = useState(editing?.level || "SERVICE");
  const [parentId, setParentId] = useState(editing?.parentComponentId || "");
  const [inclusionType, setInclusionType] = useState(editing?.inclusionType || "INCLUDE");
  const [limitType, setLimitType] = useState(editing?.limitType || "NO_LIMIT");
  const [itemLimit, setItemLimit] = useState(editing?.itemLimit != null ? String(editing.itemLimit) : "");
  const [amountLimit, setAmountLimit] = useState(editing?.amountLimit != null ? String(editing.amountLimit) : "");
  const [excessHandling, setExcessHandling] = useState(editing?.excessHandling || "OUT_OF_PACKAGE");
  const [referenceName, setReferenceName] = useState(editing?.referenceName || "");
  const [nameEdited, setNameEdited] = useState(false);
  const [saving, setSaving] = useState(false);

  // HEAD picker
  const [chargeHeads, setChargeHeads] = useState([]);
  const [chargeHeadId, setChargeHeadId] = useState(editing?.chargeHeadId || "");
  const [newHeadCode, setNewHeadCode] = useState("");

  // SERVICE_GROUP / CLASSIFICATION / SERVICE cascade
  const [groups, setGroups] = useState([]);
  const [groupId, setGroupId] = useState(editing?.serviceGroupId || "");
  const [classifications, setClassifications] = useState([]);
  const [classificationId, setClassificationId] = useState(editing?.serviceClassificationId || "");
  const [services, setServices] = useState([]);
  const [serviceSearch, setServiceSearch] = useState("");
  const [medicalServiceId, setMedicalServiceId] = useState(editing?.medicalServiceId || "");
  // Ticking this swaps the group-then-classification browse for the list of doctors'
  // consultation fees, which live outside that tree. The component is still saved as an
  // ordinary SERVICE - only the way you find it changes.
  // A consultation-level SERVICE is saved with the service alone and no group, so that is
  // how one is recognised when it comes back for editing.
  const [isConsultation, setIsConsultation] = useState(
    Boolean(editing && editing.level === "SERVICE" && editing.medicalServiceId && !editing.serviceGroupId)
  );

  // PRODUCT / ROOM_CATEGORY — no catalog wired yet, manual id entry
  const [manualRefId, setManualRefId] = useState(
    editing?.medicalProductId || editing?.roomCategoryId || ""
  );

  // Bed type — only meaningful for a Bed Charges component (entitlement matches one bed
  // class only; blank = any bed). Hidden for every other service group.
  const [bedTypes, setBedTypes] = useState([]);
  const [bedTypeCode, setBedTypeCode] = useState(editing?.bedType || "");

  useEffect(() => {
    let cancelled = false;
    listLookups({ activeOnly: true })
      .then(({ bedTypes: bt }) => { if (!cancelled) setBedTypes(bt); })
      .catch(() => { /* non-fatal */ });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (level !== "HEAD") return;
    (async () => {
      try { setChargeHeads(await listChargeHeads()); } catch { setChargeHeads([]); }
    })();
  }, [level]);

  useEffect(() => {
    if (!["SERVICE_GROUP", "CLASSIFICATION", "SERVICE"].includes(level)) return;
    (async () => {
      try { setGroups(await listServiceGroups()); } catch { setGroups([]); }
    })();
  }, [level]);

  // Changing the group or the level invalidates a classification already chosen — but the
  // first run is where an edit's saved selection arrives, so that one is left alone.
  const classificationScope = `${level}|${groupId}`;
  const lastClassificationScope = useRef(classificationScope);
  useEffect(() => {
    if (lastClassificationScope.current !== classificationScope) {
      lastClassificationScope.current = classificationScope;
      setClassificationId("");
    }
    setClassifications([]);
    if (!groupId || !["CLASSIFICATION", "SERVICE"].includes(level)) return;
    let cancelled = false;
    (async () => {
      try {
        const cls = await listServiceClassifications(groupId);
        if (!cancelled) setClassifications(cls);
      } catch { if (!cancelled) setClassifications([]); }
    })();
    return () => { cancelled = true; };
  }, [classificationScope, groupId, level]);

  useEffect(() => {
    setServices([]);
    if (level !== "SERVICE") return;
    if (!isConsultation && !groupId) return;
    let cancelled = false;
    (async () => {
      try {
        const svcs = isConsultation
          ? await listMedicalServices({ doctorConsultation: true, search: serviceSearch || undefined })
          : await listMedicalServices({ groupId, classificationId: classificationId || undefined, search: serviceSearch || undefined });
        if (!cancelled) setServices(svcs);
      } catch { if (!cancelled) setServices([]); }
    })();
    return () => { cancelled = true; };
  }, [level, groupId, classificationId, serviceSearch, isConsultation]);

  // Leaving SERVICE level, or switching how services are listed, drops a stale selection —
  // again not on the first run, which is when an edit's own service is put into the form.
  const serviceScope = `${level}|${isConsultation}`;
  const lastServiceScope = useRef(serviceScope);
  useEffect(() => {
    if (lastServiceScope.current !== serviceScope) {
      lastServiceScope.current = serviceScope;
      setMedicalServiceId("");
    }
    if (level !== "SERVICE") setIsConsultation(false);
  }, [serviceScope, level]);

  // An edit of a SERVICE component arrives with the service chosen but no group to list it
  // from, so the dropdown would show nothing and read as though no service were set. Keep
  // the saved one as an option until the catalogue list actually carries it.
  const serviceOptions = useMemo(() => {
    const chosen = editing && editing.level === "SERVICE" ? editing.medicalServiceId : null;
    if (!chosen || services.some((svc) => String(svc.id) === String(chosen))) return services;
    return [{ id: chosen, name: editing.referenceName || "Service already chosen" }, ...services];
  }, [services, editing]);

  // Display name auto-fills from whichever reference the current level selects.
  // Compared with String() on both sides — MUI select values come back numeric
  // for numeric ids, so a raw === against a string id would silently miss.
  const autoName = useMemo(() => {
    const groupName = groups.find((g) => String(g.id) === String(groupId))?.name || "";
    const classificationName = classifications.find((c) => String(c.id) === String(classificationId))?.name || "";
    if (level === "HEAD") return chargeHeads.find((h) => String(h.id) === String(chargeHeadId))?.name || "";
    if (level === "SERVICE") {
      // Cascade down to the most specific selection: service → classification → group.
      const serviceName = services.find((s) => String(s.id) === String(medicalServiceId))?.name || "";
      return serviceName || classificationName || groupName;
    }
    if (level === "SERVICE_GROUP") return groupName;
    if (level === "CLASSIFICATION") return classificationName;
    return "";
  }, [level, chargeHeads, chargeHeadId, services, medicalServiceId, groups, groupId, classifications, classificationId]);

  // Keep the field mirrored to the selection until the user types their own name. An empty
  // autoName means nothing has resolved yet — the catalogue is still loading on an edit —
  // and must not wipe the name already in the field.
  useEffect(() => {
    if (!nameEdited && autoName) setReferenceName(autoName);
  }, [autoName, nameEdited]);

  // Bed type only applies to the Bed Charges group; show the picker only then.
  const selectedGroup = groups.find((g) => String(g.id) === String(groupId));
  const isBedGroup = Boolean(
    selectedGroup &&
      /bed/i.test(`${selectedGroup.code || ""} ${selectedGroup.name || ""}`)
  );
  // Clear the bed type only once the chosen group is known not to be a bed group. On an
  // edit the groups list has not arrived on the first run, so an unconditional clear would
  // throw the saved bed type away before anyone saw it.
  useEffect(() => {
    if (selectedGroup && !isBedGroup) setBedTypeCode("");
  }, [selectedGroup, isBedGroup]);

  const createHead = async () => {
    if (!newHeadCode.trim()) return;
    try {
      const head = await createChargeHead({ head_code: newHeadCode.trim(), head_name: newHeadCode.trim() });
      toast.success(`Charge head "${head.name}" created`);
      setChargeHeads((list) => [...list, head]);
      setChargeHeadId(head.id);
      setNewHeadCode("");
    } catch (err) {
      toast.error(toBillingError(err, "Could not create charge head"));
    }
  };

  const resetForm = () => {
    setChargeHeadId(""); setGroupId(""); setClassificationId(""); setMedicalServiceId("");
    setManualRefId(""); setBedTypeCode(""); setReferenceName(""); setNameEdited(false); setItemLimit(""); setAmountLimit("");
    setLimitType("NO_LIMIT"); setInclusionType("INCLUDE"); setExcessHandling("OUT_OF_PACKAGE"); setParentId("");
  };

  const missingTarget = (() => {
    if (level === "HEAD" && !chargeHeadId) return "Pick a charge head first.";
    if (level === "SERVICE_GROUP" && !groupId) return "Pick a service group first.";
    if (level === "CLASSIFICATION" && !classificationId) return "Pick a classification first.";
    if (level === "SERVICE" && !medicalServiceId) return "Pick a service first.";
    if (level === "PRODUCT" && !manualRefId) return "Enter the product id first.";
    if (level === "ROOM_CATEGORY" && !manualRefId) return "Enter the room category id first.";
    return "";
  })();

  const submit = async () => {
    if (missingTarget) {
      toast.error(missingTarget);
      return;
    }
    if (limitType === "ITEM_LIMIT" && !String(itemLimit).trim()) {
      toast.error("Item limit is required for an item limit"); return;
    }
    if (limitType === "AMOUNT_LIMIT" && !String(amountLimit).trim()) {
      toast.error("Amount limit is required for an amount limit"); return;
    }
    const body = {
      component_level: level,
      parent_component_id: parentId || null,
      inclusion_type: inclusionType,
      limit_type: limitType,
      item_limit: limitType === "ITEM_LIMIT" ? itemLimit : "",
      amount_limit: limitType === "AMOUNT_LIMIT" ? amountLimit : "",
      excess_handling: excessHandling,
      reference_name_snapshot: referenceName.trim() || undefined,
    };
    if (level === "HEAD") body.charge_head_id = chargeHeadId;
    if (level === "SERVICE_GROUP") body.service_group_id = groupId;
    if (level === "CLASSIFICATION") body.service_classification_id = classificationId;
    if (level === "SERVICE") body.medical_service_id = medicalServiceId;
    if (level === "PRODUCT") body.medical_product_id = manualRefId;
    if (level === "ROOM_CATEGORY") body.room_category_id = manualRefId;
    if (isBedGroup && bedTypeCode) body.bed_type_code = bedTypeCode;

    // On an edit every pointer goes up, the ones this level does not use as null, so a
    // component that has been re-pointed stops matching whatever it used to match. A
    // component is matched on what it points at, so a leftover id would quietly widen the
    // package instead of moving it.
    if (editing) {
      body.charge_head_id = level === "HEAD" ? chargeHeadId : null;
      body.service_group_id = level === "SERVICE_GROUP" ? groupId : null;
      body.service_classification_id = level === "CLASSIFICATION" ? classificationId : null;
      body.medical_service_id = level === "SERVICE" ? medicalServiceId : null;
      body.medical_product_id = level === "PRODUCT" ? manualRefId : null;
      body.room_category_id = level === "ROOM_CATEGORY" ? manualRefId : null;
      body.bed_type_code = isBedGroup && bedTypeCode ? bedTypeCode : null;
    }

    if (!body.reference_name_snapshot) {
      if (level === "HEAD") body.reference_name_snapshot = chargeHeads.find((h) => h.id === chargeHeadId)?.name;
      if (level === "SERVICE") body.reference_name_snapshot = services.find((s) => String(s.id) === String(medicalServiceId))?.name;
      if (level === "SERVICE_GROUP") body.reference_name_snapshot = groups.find((g) => g.id === groupId)?.name;
      if (level === "CLASSIFICATION") body.reference_name_snapshot = classifications.find((c) => c.id === classificationId)?.name;
    }

    setSaving(true);
    try {
      if (editing) {
        await updatePackageComponent(editing.id, body);
        toast.success("Component updated");
        onSaved();
        if (onCancelEdit) onCancelEdit();
      } else {
        await createPackageComponent(versionId, body);
        toast.success("Component added");
        resetForm();
        onSaved();
      }
    } catch (err) {
      toast.error(toBillingError(
        err, editing ? "Could not update component" : "Could not add component",
      ));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ padding: "14px",
      backgroundColor: editing ? IPD_COLORS.info.bg : IPD_COLORS.pageBg,
      border: `1px solid ${editing ? IPD_COLORS.info.border : C.border}`,
      borderRadius: "10px", marginBottom: "16px" }}>
      {!!editing && (
        <Box sx={{ fontSize: "12px", fontWeight: 700, color: C.text, marginBottom: "10px" }}>
          Editing “{editing.referenceName}” — change what it covers, its limits or its name
        </Box>
      )}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "12px", marginBottom: "12px" }}>
        <IpdFormField label="Level">
          <IpdTextField select fullWidth value={level} onChange={(e) => setLevel(e.target.value)}>
            {COMPONENT_LEVELS.map((l) => <MenuItem key={l} value={l}>{l.replace(/_/g, " ")}</MenuItem>)}
          </IpdTextField>
        </IpdFormField>
        <IpdFormField label="Parent (optional)">
          <IpdTextField select fullWidth value={parentId} onChange={(e) => setParentId(e.target.value)}>
            <MenuItem value="">None (top-level)</MenuItem>
            {existingComponents.map((c) => <MenuItem key={c.id} value={c.id}>{c.referenceName} ({c.level})</MenuItem>)}
          </IpdTextField>
        </IpdFormField>
        <IpdFormField label="Inclusion">
          <IpdTextField select fullWidth value={inclusionType} onChange={(e) => setInclusionType(e.target.value)}>
            <MenuItem value="INCLUDE">INCLUDE</MenuItem>
            <MenuItem value="EXCLUDE">EXCLUDE</MenuItem>
          </IpdTextField>
        </IpdFormField>
      </Box>

      {/* Level-specific reference picker */}
      {level === "HEAD" && (
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "12px", marginBottom: "12px" }}>
          <IpdFormField label="Charge head">
            <IpdTextField select fullWidth value={chargeHeadId} onChange={(e) => setChargeHeadId(e.target.value)}>
              <MenuItem value="">Select charge head…</MenuItem>
              {chargeHeads.map((h) => <MenuItem key={h.id} value={h.id}>{h.name}</MenuItem>)}
            </IpdTextField>
          </IpdFormField>
          <IpdFormField label="Or create new">
            <Box sx={{ display: "flex", gap: "6px" }}>
              <IpdTextField fullWidth value={newHeadCode} onChange={(e) => setNewHeadCode(e.target.value)} placeholder="INVESTIGATION" />
              <IpdButton variant="outlined" onClick={createHead} sx={{ flexShrink: 0 }}>Add</IpdButton>
            </Box>
          </IpdFormField>
        </Box>
      )}

      {["SERVICE_GROUP", "CLASSIFICATION", "SERVICE"].includes(level) && (
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "12px", marginBottom: "12px" }}>
          <IpdFormField label="Service group">
            <IpdTextField select fullWidth value={groupId} onChange={(e) => setGroupId(e.target.value)} disabled={isConsultation}>
              <MenuItem value="">{isConsultation ? "Not needed for a consultation" : "Select group…"}</MenuItem>
              {groups.map((g) => <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>)}
            </IpdTextField>
          </IpdFormField>
          {["CLASSIFICATION", "SERVICE"].includes(level) && (
            <IpdFormField label={`Classification${level === "SERVICE" ? " (optional)" : ""}`}>
              <IpdTextField select fullWidth value={classificationId} onChange={(e) => setClassificationId(e.target.value)} disabled={!groupId || isConsultation}>
                <MenuItem value="">{isConsultation ? "Not needed for a consultation" : groupId ? "All classifications" : "Select group first"}</MenuItem>
                {classifications.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
              </IpdTextField>
            </IpdFormField>
          )}
          {(isBedGroup || !!bedTypeCode) && (
            <IpdFormField label="Bed type (blank = any bed)">
              <IpdTextField select fullWidth value={bedTypeCode} onChange={(e) => setBedTypeCode(e.target.value)}>
                <MenuItem value="">Any bed type</MenuItem>
                {bedTypes.map((b) => <MenuItem key={b.id} value={b.code}>{b.name} ({b.code})</MenuItem>)}
              </IpdTextField>
            </IpdFormField>
          )}
          {level === "SERVICE" && (
            <>
              <IpdFormField label="Doctor consultation">
                <FormControlLabel
                  control={<Checkbox checked={isConsultation} onChange={(e) => setIsConsultation(e.target.checked)} />}
                  label="This is a doctor's consultation fee"
                />
              </IpdFormField>
              <IpdFormField label="Filter by name">
                <IpdTextField
                  fullWidth
                  value={serviceSearch}
                  onChange={(e) => setServiceSearch(e.target.value)}
                  placeholder={isConsultation ? "doctor name…" : "cbc, chest…"}
                  disabled={!groupId && !isConsultation}
                />
              </IpdFormField>
              <IpdFormField label={isConsultation ? "Consultation" : "Service"}>
                <IpdTextField
                  select
                  fullWidth
                  value={medicalServiceId}
                  onChange={(e) => setMedicalServiceId(e.target.value)}
                  disabled={!groupId && !isConsultation && serviceOptions.length === 0}
                >
                  <MenuItem value="">{serviceOptions.length ? "Select…" : "No services loaded"}</MenuItem>
                  {serviceOptions.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name}
                      {s.doctorName ? ` — ${s.doctorName}` : ""}
                      {s.fee != null ? ` (${fmtINR(s.fee)})` : s.code ? ` (${s.code})` : ""}
                    </MenuItem>
                  ))}
                </IpdTextField>
              </IpdFormField>
            </>
          )}
        </Box>
      )}

      {["PRODUCT", "ROOM_CATEGORY"].includes(level) && (
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "12px", marginBottom: "12px" }}>
          <IpdFormField label={`${level === "PRODUCT" ? "Product ID" : "Room category ID"} (manual — catalog not wired yet)`}>
            <IpdTextField fullWidth value={manualRefId} onChange={(e) => setManualRefId(e.target.value)} />
          </IpdFormField>
        </Box>
      )}

      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "12px", marginBottom: "12px" }}>
        <IpdFormField label="Display name">
          <IpdTextField fullWidth value={referenceName} onChange={(e) => { setReferenceName(e.target.value); setNameEdited(true); }} placeholder="Auto-filled from selection above" />
        </IpdFormField>
        <IpdFormField label="Limit type">
          <IpdTextField select fullWidth value={limitType} onChange={(e) => setLimitType(e.target.value)}>
            {LIMIT_TYPES.map((t) => <MenuItem key={t} value={t}>{t.replace(/_/g, " ")}</MenuItem>)}
          </IpdTextField>
        </IpdFormField>
        {limitType === "ITEM_LIMIT" && (
          <IpdFormField label="Item limit">
            <IpdTextField fullWidth type="number" value={itemLimit} onChange={(e) => setItemLimit(e.target.value)} placeholder="2" />
          </IpdFormField>
        )}
        {limitType === "AMOUNT_LIMIT" && (
          <IpdFormField label="Amount limit (₹)">
            <IpdTextField fullWidth type="number" value={amountLimit} onChange={(e) => setAmountLimit(e.target.value)} placeholder="5000" />
          </IpdFormField>
        )}
        <IpdFormField label="On excess / exclusion">
          <IpdTextField select fullWidth value={excessHandling} onChange={(e) => setExcessHandling(e.target.value)}>
            {EXCESS_HANDLING.map((t) => <MenuItem key={t} value={t}>{t.replace(/_/g, " ")}</MenuItem>)}
          </IpdTextField>
        </IpdFormField>
      </Box>

      <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
        <IpdButton
          onClick={submit}
          loading={saving}
          disabled={!!missingTarget}
          title={missingTarget || (editing
            ? "Save this component"
            : "Add this component to the package")}
        >
          {editing ? "Save changes" : "Add component"}
        </IpdButton>
        {!!editing && (
          <IpdButton variant="outlined" onClick={onCancelEdit} disabled={saving}>
            Cancel
          </IpdButton>
        )}
      </Box>
      {!!missingTarget && (
        <Typography sx={{ marginTop: "6px", fontSize: "12px", color: C.textSub }}>
          {missingTarget} A component is matched on what it points at, not on its name, so one
          without a target would cover nothing.
        </Typography>
      )}
    </Box>
  );
}


function ComponentsTab({ versionId, isDraft }) {
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [editComponent, setEditComponent] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setComponents(await listPackageComponents(versionId)); } catch { setComponents([]); }
    finally { setLoading(false); }
  }, [versionId]);

  useEffect(() => { load(); }, [load]);

  const remove = async (c) => {
    setBusyId(c.id);
    try {
      await deletePackageComponent(c.id);
      toast.success("Component removed");
      await load();
    } catch (err) {
      toast.error(toBillingError(err, "Could not remove component"));
    } finally {
      setBusyId(null);
    }
  };

  const byId = new Map(components.map((c) => [c.id, c]));

  // A component may not be re-parented under itself or one of its own descendants —
  // that would build a cycle the tree walk can only bail out of, not render.
  const parentOptions = editComponent
    ? components.filter((c) => {
        if (c.id === editComponent.id) return false;
        for (let node = c, hops = 0; node && hops <= 8; hops += 1) {
          if (node.parentComponentId === editComponent.id) return false;
          node = byId.get(node.parentComponentId);
        }
        return true;
      })
    : components;

  const limitLabel = (c) => {
    if (c.limitType === "ITEM_LIMIT") return `Max ${c.itemLimit ?? "—"} item(s)`;
    if (c.limitType === "AMOUNT_LIMIT") return `Max ${fmtINR(c.amountLimit)}`;
    return "No limit";
  };

  // Which target a level needs, so a row that is missing it can say so.
  const TARGET_LABEL_BY_LEVEL = {
    HEAD: "charge head",
    SERVICE_GROUP: "service group",
    CLASSIFICATION: "classification",
    SERVICE: "service",
    PRODUCT: "product",
    ROOM_CATEGORY: "room category",
  };
  const TARGET_FIELD_BY_LEVEL = {
    HEAD: "chargeHeadId",
    SERVICE_GROUP: "serviceGroupId",
    CLASSIFICATION: "serviceClassificationId",
    SERVICE: "medicalServiceId",
    PRODUCT: "medicalProductId",
    ROOM_CATEGORY: "roomCategoryId",
  };
  const coversNothing = (row) => {
    const field = TARGET_FIELD_BY_LEVEL[row.level];
    if (!field) return "";
    const value = row[field];
    return value === null || value === undefined || value === "" ? TARGET_LABEL_BY_LEVEL[row.level] : "";
  };

  const columns = [
    { field: "referenceName", headerName: "Component", flex: 1.5, minWidth: 180, sortable: false,
      renderCell: (p) => {
        const depth = componentDepth(p.row, byId);
        const unwired = coversNothing(p.row);
        return (
          <Box component="span" sx={{ pl: `${depth * 20}px`, fontSize: "13px", color: C.text }}>
            {depth > 0 && <Box component="span" sx={{ color: C.textSub, marginRight: "4px" }}>↳</Box>}
            {p.value}
            {unwired && (
              <Box
                component="span"
                title={`No ${unwired} was chosen, so this covers no charge. Edit it and pick one, or switch it off.`}
                sx={{ marginLeft: "6px", padding: "1px 6px", borderRadius: "999px", fontSize: "10px",
                      fontWeight: 700, backgroundColor: IPD_COLORS.error.bg, color: IPD_COLORS.error.text }}
              >
                covers nothing
              </Box>
            )}
          </Box>
        );
      } },
    { field: "level", headerName: "Level", flex: 1, minWidth: 120, sortable: false,
      renderCell: (p) => <Box component="span" sx={{ fontSize: "11px", color: C.textSub }}>{p.value.replace(/_/g, " ")}</Box> },
    { field: "inclusionType", headerName: "Inclusion", flex: 1, minWidth: 110, sortable: false,
      renderCell: (p) => (
        <Box component="span" sx={{ padding: "2px 8px", borderRadius: "999px", fontSize: "11px", fontWeight: 700,
          backgroundColor: p.value === "INCLUDE" ? IPD_COLORS.success.bg : IPD_COLORS.error.bg,
          color: p.value === "INCLUDE" ? IPD_COLORS.success.text : IPD_COLORS.error.text }}>
          {p.value}
        </Box>
      ) },
    { field: "limit", headerName: "Limit", flex: 1, minWidth: 120, sortable: false,
      renderCell: (p) => <Box component="span" sx={{ fontSize: "12px", color: C.textSub }}>{limitLabel(p.row)}</Box> },
    { field: "excessHandling", headerName: "On excess", flex: 1, minWidth: 130, sortable: false,
      renderCell: (p) => <Box component="span" sx={{ fontSize: "12px", color: C.textSub }}>{p.value.replace(/_/g, " ")}</Box> },
    { field: "actions", headerName: "", width: 92, sortable: false, align: "right", headerAlign: "right",
      renderCell: (p) => (
        <Box sx={{ display: "flex", gap: "2px" }}>
          <IconButton size="small" onClick={() => setEditComponent(p.row)} disabled={busyId === p.row.id} title="Edit">
            <SvgIcon name="edit" size={16} color={C.primary} />
          </IconButton>
          <IconButton size="small" onClick={() => remove(p.row)} disabled={busyId === p.row.id} title="Remove">
            <SvgIcon name="trash" size={16} color={C.danger} />
          </IconButton>
        </Box>
      ) },
  ];

  return (
    <IpdSectionCard title="Components" subtitle="Included / excluded services with limits — builds the entitlement tree">
      {/* One form for both jobs. The key remounts it when the row being edited changes,
          so every field starts from that component instead of the one before it. */}
      <ComponentForm
        key={editComponent ? editComponent.id : "new-component"}
        versionId={versionId}
        existingComponents={parentOptions}
        editing={editComponent}
        onSaved={load}
        onCancelEdit={() => setEditComponent(null)}
      />

      <IpdDataGrid
        embedded
        hideFooter
        disableColumnMenu
        loading={loading}
        rows={components}
        columns={columns}
        emptyMessage="No components yet — add included/excluded services above."
      />
    </IpdSectionCard>
  );
}

// ── Inventory / Pharmacy bucket tab ──────────────────────────────────────────
// A capped bucket of pharmacy/inventory charges: matching charges fold into the
// package up to an amount cap (net 0), the excess bills out-of-package, and
// excluded items always bill separately. All rows are package components with an
// inventory component_level; the cap carries AMOUNT_LIMIT, exclusions NO_LIMIT.
const INVENTORY_SIMPLE_LEVELS = ["ALL_PHARMACY", "ALL_CONSUMABLE", "ALL_INVENTORY"];
const INVENTORY_GRANULAR_LEVELS = ["MATERIAL_GROUP", "ITEM_CLASS", "INVENTORY_CATEGORY", "INVENTORY_ITEM"];
const INVENTORY_EXCLUDE_LEVELS = ["INVENTORY_ITEM", "INVENTORY_CATEGORY"];
const INVENTORY_LEVELS = [...INVENTORY_SIMPLE_LEVELS, ...INVENTORY_GRANULAR_LEVELS];
const levelLabel = (level) => (level || "").replace(/_/g, " ");

// Scope picker for a single granular/exclusion level: a select from the loaded
// master list, or (for INVENTORY_ITEM) a debounced item search + select.
// Reports the picked { code, name } back to the parent.
function InventoryCodePicker({ level, code, onPick, materialGroups, itemClasses, categories }) {
  const [itemSearch, setItemSearch] = useState("");
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (level !== "INVENTORY_ITEM") return undefined;
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const rows = await searchInventoryItems(itemSearch || undefined);
        if (!cancelled) setItems(rows);
      } catch { if (!cancelled) setItems([]); }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [level, itemSearch]);

  if (level === "INVENTORY_ITEM") {
    return (
      <>
        <IpdFormField label="Search item" sx={{ flex: "1 1 150px" }}>
          <IpdTextField fullWidth value={itemSearch} onChange={(e) => setItemSearch(e.target.value)} placeholder="paracetamol, syringe…" />
        </IpdFormField>
        <IpdFormField label="Item" sx={{ flex: "1 1 150px" }}>
          <IpdTextField
            select
            fullWidth
            value={code}
            onChange={(e) => {
              const it = items.find((x) => x.code === e.target.value);
              onPick({ code: e.target.value, name: it?.name || "" });
            }}
          >
            <MenuItem value="">{items.length ? "Select item…" : "Type to search"}</MenuItem>
            {items.map((it) => <MenuItem key={it.id} value={it.code}>{it.name}{it.code ? ` (${it.code})` : ""}</MenuItem>)}
          </IpdTextField>
        </IpdFormField>
      </>
    );
  }

  const list = level === "MATERIAL_GROUP" ? materialGroups : level === "ITEM_CLASS" ? itemClasses : categories;
  const label = level === "MATERIAL_GROUP" ? "Material group" : level === "ITEM_CLASS" ? "Item class" : "Category";
  return (
    <IpdFormField label={label} sx={{ flex: "1 1 150px" }}>
      <IpdTextField
        select
        fullWidth
        value={code}
        onChange={(e) => {
          const row = list.find((x) => x.code === e.target.value);
          onPick({ code: e.target.value, name: row?.name || "" });
        }}
      >
        <MenuItem value="">Select {label.toLowerCase()}…</MenuItem>
        {list.map((row) => <MenuItem key={row.id} value={row.code}>{row.name}{row.code ? ` (${row.code})` : ""}</MenuItem>)}
      </IpdTextField>
    </IpdFormField>
  );
}

function PharmacyBucketTab({ versionId }) {
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const [materialGroups, setMaterialGroups] = useState([]);
  const [itemClasses, setItemClasses] = useState([]);
  const [categories, setCategories] = useState([]);

  // Cap form
  const [capAmount, setCapAmount] = useState("");
  const [scopeMode, setScopeMode] = useState("SIMPLE"); // SIMPLE | GRANULAR
  const [simpleLevel, setSimpleLevel] = useState("ALL_PHARMACY");
  const [granularLevel, setGranularLevel] = useState("MATERIAL_GROUP");
  const [granularPick, setGranularPick] = useState({ code: "", name: "" });
  const [savingCap, setSavingCap] = useState(false);
  const [editingCapId, setEditingCapId] = useState(null);

  // Exclusion form
  const [exclLevel, setExclLevel] = useState("INVENTORY_ITEM");
  const [exclPick, setExclPick] = useState({ code: "", name: "" });
  const [savingExcl, setSavingExcl] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setComponents(await listPackageComponents(versionId)); }
    catch { setComponents([]); }
    finally { setLoading(false); }
  }, [versionId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      listInventoryMaterialGroups().catch(() => []),
      listInventoryItemClasses().catch(() => []),
      listInventoryCategories().catch(() => []),
    ]).then(([g, c, cat]) => {
      if (cancelled) return;
      setMaterialGroups(g); setItemClasses(c); setCategories(cat);
    });
    return () => { cancelled = true; };
  }, []);

  const bucketRows = components.filter((c) => INVENTORY_LEVELS.includes(c.level));

  const resetCapForm = () => {
    setCapAmount(""); setScopeMode("SIMPLE"); setSimpleLevel("ALL_PHARMACY");
    setGranularLevel("MATERIAL_GROUP"); setGranularPick({ code: "", name: "" }); setEditingCapId(null);
  };

  const submitCap = async () => {
    if (!capAmount || Number(capAmount) <= 0) { toast.error("Enter a cap amount"); return; }
    const isSimple = scopeMode === "SIMPLE";
    const level = isSimple ? simpleLevel : granularLevel;
    const scopeCode = isSimple ? undefined : granularPick.code;
    if (!isSimple && !scopeCode) { toast.error("Pick a scope for the granular cap"); return; }
    setSavingCap(true);
    try {
      if (editingCapId) {
        await updatePackageComponent(editingCapId, {
          component_level: level,
          amount_limit: capAmount,
          inventory_scope_code: scopeCode || null,
        });
        toast.success("Cap updated");
      } else {
        await createPackageComponent(versionId, {
          component_level: level,
          inclusion_type: "INCLUDE",
          limit_type: "AMOUNT_LIMIT",
          amount_limit: capAmount,
          limit_scope: "PACKAGE",
          excess_handling: "OUT_OF_PACKAGE",
          inventory_scope_code: scopeCode,
          reference_name_snapshot: isSimple ? levelLabel(level) : granularPick.name || scopeCode,
        });
        toast.success("Pharmacy / inventory cap added");
      }
      resetCapForm();
      await load();
    } catch (err) {
      toast.error(toBillingError(err, "Could not save cap"));
    } finally {
      setSavingCap(false);
    }
  };

  const editCap = (row) => {
    setEditingCapId(row.id);
    setCapAmount(row.amountLimit != null ? String(row.amountLimit) : "");
    if (INVENTORY_SIMPLE_LEVELS.includes(row.level)) {
      setScopeMode("SIMPLE"); setSimpleLevel(row.level);
    } else {
      setScopeMode("GRANULAR"); setGranularLevel(row.level);
      setGranularPick({ code: row.inventoryScopeCode || "", name: row.referenceName || "" });
    }
  };

  const submitExclusion = async () => {
    if (!exclPick.code) { toast.error("Pick an item or category to exclude"); return; }
    setSavingExcl(true);
    try {
      await createPackageComponent(versionId, {
        component_level: exclLevel,
        inclusion_type: "EXCLUDE",
        inventory_scope_code: exclPick.code,
        limit_type: "NO_LIMIT",
        reference_name_snapshot: exclPick.name || exclPick.code,
      });
      toast.success("Exclusion added");
      setExclPick({ code: "", name: "" });
      await load();
    } catch (err) {
      toast.error(toBillingError(err, "Could not add exclusion"));
    } finally {
      setSavingExcl(false);
    }
  };

  const remove = async (row) => {
    setBusyId(row.id);
    try {
      await deletePackageComponent(row.id);
      toast.success("Removed");
      if (row.id === editingCapId) resetCapForm();
      await load();
    } catch (err) {
      toast.error(toBillingError(err, "Could not remove"));
    } finally {
      setBusyId(null);
    }
  };

  const columns = [
    { field: "level", headerName: "Scope", flex: 1, minWidth: 150, sortable: false,
      renderCell: (p) => <Box component="span" sx={{ fontSize: "12px", fontWeight: 700, color: C.text }}>{levelLabel(p.value)}</Box> },
    { field: "referenceName", headerName: "Item / group", flex: 1.4, minWidth: 170, sortable: false,
      renderCell: (p) => (
        <Box component="span" sx={{ fontSize: "12px", color: C.text }}>
          {p.value || "—"}
          {p.row.inventoryScopeCode && <Box component="span" sx={{ color: C.textSub, marginLeft: "6px" }}>({p.row.inventoryScopeCode})</Box>}
        </Box>
      ) },
    { field: "inclusionType", headerName: "Inclusion", flex: 1, minWidth: 110, sortable: false,
      renderCell: (p) => (
        <Box component="span" sx={{ padding: "2px 8px", borderRadius: "999px", fontSize: "11px", fontWeight: 700,
          backgroundColor: p.value === "INCLUDE" ? IPD_COLORS.success.bg : IPD_COLORS.error.bg,
          color: p.value === "INCLUDE" ? IPD_COLORS.success.text : IPD_COLORS.error.text }}>
          {p.value}
        </Box>
      ) },
    { field: "amountLimit", headerName: "Cap", flex: 1, minWidth: 110, sortable: false, align: "right", headerAlign: "right",
      renderCell: (p) => <Box component="span" sx={{ fontSize: "12px", color: C.textSub }}>{p.row.limitType === "AMOUNT_LIMIT" ? fmtINR(p.value) : "—"}</Box> },
    { field: "actions", headerName: "", width: 130, sortable: false, align: "right", headerAlign: "right",
      renderCell: (p) => (
        <Box sx={{ display: "flex", gap: "4px", justifyContent: "flex-end" }}>
          {p.row.inclusionType === "INCLUDE" && (
            <IpdButton variant="outlined" size="small" onClick={() => editCap(p.row)}>Edit</IpdButton>
          )}
          <IconButton size="small" onClick={() => remove(p.row)} disabled={busyId === p.row.id} title="Remove">
            <SvgIcon name="trash" size={16} color={C.danger} />
          </IconButton>
        </Box>
      ) },
  ];

  const modeBtn = (id, label) => (
    <IpdButton key={id} variant={scopeMode === id ? "contained" : "outlined"} size="small" onClick={() => setScopeMode(id)}>
      {label}
    </IpdButton>
  );

  return (
    <IpdSectionCard
      title="Inventory / Pharmacy bucket"
      subtitle="A capped bucket of pharmacy/inventory charges — matching charges fold into the package up to the cap; the excess bills out-of-package; exclusions always bill separately. Cap is per admission."
    >
      {/* Cap component */}
      <Box sx={{ padding: "14px", backgroundColor: IPD_COLORS.pageBg, border: `1px solid ${C.border}`, borderRadius: "10px", marginBottom: "16px" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px", flexWrap: "wrap", gap: "8px" }}>
          <Typography sx={{ fontSize: "13px", fontWeight: 700, color: C.text }}>{editingCapId ? "Edit cap" : "Add cap"}</Typography>
          <Box sx={{ display: "flex", gap: "6px" }}>
            {modeBtn("SIMPLE", "Simple toggle")}
            {modeBtn("GRANULAR", "Granular picker")}
          </Box>
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "12px", marginBottom: "12px" }}>
          <IpdFormField label="Cap amount (₹)">
            <IpdTextField fullWidth type="number" value={capAmount} onChange={(e) => setCapAmount(e.target.value)} placeholder="10000" />
          </IpdFormField>

          {scopeMode === "SIMPLE" ? (
            <IpdFormField label="Applies to">
              <IpdTextField select fullWidth value={simpleLevel} onChange={(e) => setSimpleLevel(e.target.value)}>
                {INVENTORY_SIMPLE_LEVELS.map((l) => <MenuItem key={l} value={l}>{levelLabel(l)}</MenuItem>)}
              </IpdTextField>
            </IpdFormField>
          ) : (
            <>
              <IpdFormField label="Scope level">
                <IpdTextField
                  select
                  fullWidth
                  value={granularLevel}
                  onChange={(e) => { setGranularLevel(e.target.value); setGranularPick({ code: "", name: "" }); }}
                >
                  {INVENTORY_GRANULAR_LEVELS.map((l) => <MenuItem key={l} value={l}>{levelLabel(l)}</MenuItem>)}
                </IpdTextField>
              </IpdFormField>
              <InventoryCodePicker
                level={granularLevel}
                code={granularPick.code}
                onPick={setGranularPick}
                materialGroups={materialGroups}
                itemClasses={itemClasses}
                categories={categories}
              />
            </>
          )}
        </Box>

        <Box sx={{ display: "flex", gap: "8px" }}>
          <IpdButton onClick={submitCap} loading={savingCap}>{editingCapId ? "Save cap" : "Add cap"}</IpdButton>
          {editingCapId && <IpdButton variant="outlined" onClick={resetCapForm}>Cancel</IpdButton>}
        </Box>
      </Box>

      {/* Exclusions */}
      <Box sx={{ padding: "14px", backgroundColor: IPD_COLORS.pageBg, border: `1px solid ${C.border}`, borderRadius: "10px", marginBottom: "16px" }}>
        <Typography sx={{ fontSize: "13px", fontWeight: 700, color: C.text, marginBottom: "12px" }}>
          Exclusions — always billed separately, never counted to the cap
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: "12px", marginBottom: "12px" }}>
          <IpdFormField label="Exclude by" sx={{ flex: "1 1 150px" }}>
            <IpdTextField
              select
              fullWidth
              value={exclLevel}
              onChange={(e) => { setExclLevel(e.target.value); setExclPick({ code: "", name: "" }); }}
            >
              {INVENTORY_EXCLUDE_LEVELS.map((l) => <MenuItem key={l} value={l}>{levelLabel(l)}</MenuItem>)}
            </IpdTextField>
          </IpdFormField>
          <InventoryCodePicker
            level={exclLevel === "INVENTORY_CATEGORY" ? "INVENTORY_CATEGORY" : "INVENTORY_ITEM"}
            code={exclPick.code}
            onPick={setExclPick}
            materialGroups={materialGroups}
            itemClasses={itemClasses}
            categories={categories}
          />
          <Box sx={{ display: "flex", alignItems: "flex-end", marginLeft: "auto", paddingBottom: "20px" }}>
            <IpdButton onClick={submitExclusion} loading={savingExcl} sx={{ justifyContent: "center" }}>Add exclusion</IpdButton>
          </Box>
        </Box>
      </Box>

      <IpdDataGrid
        embedded
        hideFooter
        disableColumnMenu
        loading={loading}
        rows={bucketRows}
        columns={columns}
        emptyMessage="No inventory/pharmacy bucket rows yet — add a cap above."
      />
    </IpdSectionCard>
  );
}

// ── Publish tab ──────────────────────────────────────────────────────────────
function PublishTab({ packageId, versionId, version, onPublished }) {
  const [tariffs, setTariffs] = useState([]);
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [confirmPublish, setConfirmPublish] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, c] = await Promise.all([listPackageTariffs(versionId), listPackageComponents(versionId)]);
      setTariffs(t); setComponents(c);
    } finally { setLoading(false); }
  }, [versionId]);

  useEffect(() => { load(); }, [load]);

  const hasHospital = tariffs.some((t) => t.tariffType === "HOSPITAL" && t.active);
  const alreadyPublished = version?.status === "PUBLISHED";

  const publish = async () => {
    setPublishing(true);
    try {
      await publishPackageVersion(versionId);
      toast.success("Version published — package is now ACTIVE");
      setConfirmPublish(false);
      onPublished();
    } catch (err) {
      toast.error(toBillingError(err, "Could not publish"));
    } finally {
      setPublishing(false);
    }
  };

  return (
    <IpdSectionCard title="Publish" subtitle="Activates the package for admissions and retains version editability">
      {loading ? (
        <Box sx={{ color: C.textSub, fontSize: "13px" }}>Loading…</Box>
      ) : (
        <>
          <Box sx={{ display: "flex", gap: "24px", flexWrap: "wrap", marginBottom: "18px" }}>
            <Box>
              <Typography sx={{ fontSize: "11px", fontWeight: 700, color: C.textSub, textTransform: "uppercase" }}>Tariffs</Typography>
              <Typography sx={{ fontSize: "20px", fontWeight: 800, color: C.text }}>{tariffs.length}</Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: "11px", fontWeight: 700, color: C.textSub, textTransform: "uppercase" }}>Components</Typography>
              <Typography sx={{ fontSize: "20px", fontWeight: 800, color: C.text }}>{components.length}</Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: "11px", fontWeight: 700, color: C.textSub, textTransform: "uppercase" }}>Hospital tariff</Typography>
              <Typography sx={{ fontSize: "20px", fontWeight: 800, color: hasHospital ? C.success : C.danger }}>{hasHospital ? "Present" : "Missing"}</Typography>
            </Box>
          </Box>

          {alreadyPublished ? (
            <Box sx={{ padding: "14px 16px", backgroundColor: IPD_COLORS.success.bg, border: `1px solid ${IPD_COLORS.success.border}`, borderRadius: "8px", fontSize: "13px", color: IPD_COLORS.success.text, fontWeight: 600 }}>
              This version is published and editable for future admissions.
            </Box>
          ) : (
            <>
              {!hasHospital && <LockedNotice>Add at least one active HOSPITAL tariff before publishing.</LockedNotice>}
              <IpdButton onClick={() => setConfirmPublish(true)} disabled={!hasHospital} loading={publishing}>
                Publish version
              </IpdButton>
            </>
          )}
        </>
      )}

      <IpdConfirmDialog
        open={confirmPublish}
        title="Publish this version?"
        description="Once published, patients can be assigned against this version."
        confirmLabel="Publish"
        loading={publishing}
        onConfirm={publish}
        onCancel={() => setConfirmPublish(false)}
      />
    </IpdSectionCard>
  );
}

// ── Editor root ───────────────────────────────────────────────────────────────
export default function PackageAdminEditor({ packageId: initialId, onBack, onSaved }) {
  const [packageId, setPackageId] = useState(initialId);
  const [pkg, setPkg] = useState(null);
  const [versions, setVersions] = useState([]);
  const [currentVersionId, setCurrentVersionId] = useState(null);
  const [tab, setTab] = useState("header");
  const [loading, setLoading] = useState(Boolean(initialId));

  const load = useCallback(async () => {
    if (!packageId) return;
    setLoading(true);
    try {
      const res = await getPackage(packageId);
      setPkg(res.package);
      setVersions(res.versions);
      setCurrentVersionId((cur) => (cur && res.versions.some((v) => v.id === cur) ? cur : res.versions[0]?.id || null));
    } catch (err) {
      toast.error(toBillingError(err, "Could not load package"));
    } finally {
      setLoading(false);
    }
  }, [packageId]);

  useEffect(() => { load(); }, [load]);

  const currentVersion = versions.find((v) => v.id === currentVersionId) || null;
  const isDraft = currentVersion?.status === "DRAFT";

  const handleCreated = (newId) => {
    setPackageId(newId);
    if (onSaved) onSaved(newId);
  };

  const tabBtn = (id, label, disabled) => (
    <IpdButton
      key={id}
      variant={tab === id ? "contained" : "outlined"}
      size="small"
      onClick={() => !disabled && setTab(id)}
      disabled={disabled}
    >
      {label}
    </IpdButton>
  );

  return (
    <Box sx={{ animation: "ipdFadeIn 0.2s ease" }}>
      {packageId && !loading && pkg && (
        <Box sx={{ display: "flex", gap: "8px", marginBottom: "18px", flexWrap: "wrap" }}>
          {tabBtn("header", "Header")}
          {tabBtn("versions", "Versions")}
          {tabBtn("tariffs", "Tariffs")}
          {tabBtn("components", "Components")}
          {tabBtn("bucket", "Pharmacy bucket")}
          {tabBtn("publish", "Publish")}
        </Box>
      )}

      {(!packageId || tab === "header") && (
        <HeaderForm packageId={packageId} pkg={pkg} version={versions[0] || null} onCreated={handleCreated} onUpdated={load} />
      )}

      {packageId && loading && <Box sx={{ color: C.textSub, fontSize: "13px" }}>Loading package…</Box>}

      {packageId && !loading && tab === "versions" && (
        <VersionsTab
          packageId={packageId}
          versions={versions}
          currentVersionId={currentVersionId}
          onSelectVersion={setCurrentVersionId}
          onVersionsChanged={load}
        />
      )}

      {packageId && !loading && currentVersionId && tab === "tariffs" && (
        <TariffsTab versionId={currentVersionId} isDraft={isDraft} />
      )}

      {packageId && !loading && currentVersionId && tab === "components" && (
        <ComponentsTab versionId={currentVersionId} isDraft={isDraft} />
      )}

      {packageId && !loading && currentVersionId && tab === "bucket" && (
        <PharmacyBucketTab versionId={currentVersionId} />
      )}

      {packageId && !loading && currentVersionId && tab === "publish" && (
        <PublishTab packageId={packageId} versionId={currentVersionId} version={currentVersion} onPublished={load} />
      )}

      <Box sx={{ marginTop: "8px" }}>
        <IpdButton variant="outlined" onClick={onBack} startIcon={<SvgIcon name="back" size={15} color={C.text} />}>
          Back to packages
        </IpdButton>
      </Box>
    </Box>
  );
}
